// Pretty-name overrides only - any brand value found in pump-motor-data.json
// shows up in the dropdown automatically (see getAvailableBrands), even if
// it has no entry here. Add a line here only to give it a nicer label.
PS.motorBrandLabels = {
  te3: 'TECO TE3',
  maxe3: 'TECO MaxE3 (Hi-Eff)',
  maxev4: 'TECO MAXeV4 (IE4)',
  monarch: 'TECO Monarch (single phase)',
  w21: 'WEG W21',
  w22: 'WEG W22'
};

PS.mountingTypeLabels = {
  starline: 'Starline (close coupled)',
  sovereign: 'Sovereign (long coupled, sealed bearing)',
  isoPro: 'Iso-Pro (long coupled, oil bath)',
  pwt: 'PWT (long coupled, Pentair OEM)'
};

// The PWT range is a third-party (Pentair) OEM line that fills a gap
// between Southern Cross sizes - physically a different pump to anything
// else in the catalogue, so it's always long coupled and never offered in
// Starline/Sovereign/Iso-Pro form. SGA-2 is the 2-pole variant of SGA's
// casing, not a separate casing of its own.
PS.pwtFamilyCodes = [
  'SFG', 'SGA', 'SGA-2', 'SGH', 'SGW', 'SHK', 'SHL',
  'SJH', 'SJJ', 'SJK', 'SJL', 'SKF', 'SKG', 'SKH', 'SKJ', 'SKL'
];

PS.isPwtFamily = function (family) {
  return PS.pwtFamilyCodes.includes(PS.getDimensionPumpCode(family));
};

// Mounting type is normally a single global setting (see
// wireMountingTypeControl), but PWT pumps don't participate in that choice -
// they're always long coupled, so this overrides whatever's globally
// selected rather than letting a PWT pump's locked dropdown leak into the
// global state and affect other pumps on screen.
PS.getEffectiveMountingType = function (match) {
  return PS.isPwtFamily(match?.family) ? 'pwt' : PS.state.selectedMountingType;
};

// The raw motor catalogue, unfiltered. Only PS.getMotorsArray and the
// phase-capability check below should use this.
PS.getAllMotorRows = function () {
  return Array.isArray(PS.motorData?.motors) ? PS.motorData.motors : [];
};

// Does the motor catalogue distinguish phases at all? Every row in the file
// today is three-phase and none of them says so, which is exactly why this
// exists: until single-phase rows land, the phase filter below has nothing to
// filter on and must not silently empty the catalogue.
PS.hasMotorPhaseData = function () {
  return PS.getAllMotorRows().some(motor => motor.phases != null);
};

PS.hasSinglePhaseMotorData = function () {
  return PS.getAllMotorRows().some(motor => Number(motor.phases) === 1);
};

// Is a single-phase motor of this size actually catalogued?
//
// PS.SINGLE_PHASE_MAX_KW is 43 South's commercial limit on what they will
// supply single phase. Whether the range *stocks* a motor that big is a
// separate question, and since the 2026-08 motor rebuild the two answers no
// longer coincide: Monarch, now the only single-phase range in the file, stops
// at 3 kW - under the 4 kW policy cap. Without this check the 3-4 kW band
// offers single phase and then resolves to no motor at all, because
// selectMotorForLoad has nothing to return.
//
// Returns true when the file carries no single-phase rows at all, so this is a
// no-op for any motor file that predates Monarch.
PS.hasSinglePhaseMotorForLoad = function (poles, requiredKw) {
  if (!PS.hasSinglePhaseMotorData()) return true;
  if (!Number.isFinite(requiredKw)) return false;

  // Mirrors selectMotorForLoad's own test - smallest size at or above the load
  // - so the gate can never say yes to something the selection then can't fill.
  return PS.getAllMotorRows().some(motor =>
    Number(motor.phases) === 1
    && motor.poles === poles
    && motor.powerKw >= requiredKw
  );
};

// Every brand, size and auto-sizing lookup funnels through here, so filtering
// by the selected supply in this one place makes the whole chain phase-correct
// at once - the brand dropdown, the kW list, the auto-sized motor, and the
// bearing/CAD/dimension lookups that read the resolved motor.
//
// A complete no-op until the catalogue gains a `phases` field: with no phase
// data there is nothing to filter on, so this returns the full list exactly as
// it did before. A row that omits `phases` in an otherwise phase-aware file is
// taken as three-phase, matching what the file holds today.
PS.getMotorsArray = function () {
  const motors = PS.getAllMotorRows();
  const phase = PS.state?.selectedMotorPhase;

  if (!phase || !PS.hasMotorPhaseData()) return motors;

  return motors.filter(motor => (motor.phases == null ? 3 : Number(motor.phases)) === Number(phase));
};

PS.getAvailableBrands = function () {
  const brands = [...new Set(PS.getMotorsArray().map(motor => motor.brand))].filter(Boolean).sort();

  return brands.map(brand => ({
    value: brand,
    label: PS.motorBrandLabels[brand] || brand
  }));
};

PS.getMotorsForBrand = function (brand) {
  return PS.getMotorsArray().filter(motor => motor.brand === brand);
};

// Brand-agnostic catalogue of motor sizes, deduped by poles/kW - lets the
// customer pick a motor size before deciding which brand to buy it from,
// rather than the size choice being silently limited to whichever brand
// happens to be selected.
PS.getAllMotorSizes = function () {
  const seen = new Map();

  PS.getMotorsArray().forEach(motor => {
    const key = `${motor.poles}|${motor.powerKw.toFixed(2)}`;
    if (!seen.has(key)) {
      seen.set(key, { poles: motor.poles, powerKw: motor.powerKw });
    }
  });

  return [...seen.values()];
};

PS.getAvailablePoles = function () {
  return [...new Set(PS.getAllMotorSizes().map(size => size.poles))].sort((a, b) => a - b);
};

// Which brands actually stock a motor at this poles/kW - used once a size
// has been picked, to narrow the brand dropdown down to ones that can
// actually supply it (instead of offering every brand regardless of fit).
PS.getBrandsForMotorSize = function (poles, powerKw) {
  return PS.getAvailableBrands().filter(({ value: brand }) =>
    PS.getMotorsForBrand(brand).some(motor =>
      motor.poles === poles && Math.abs(motor.powerKw - powerKw) < 0.01
    )
  );
};

// Used by the "search by pump model" path: for each pole count this family
// actually has a curve for (matched to that pole count's synchronous
// speed), lists every catalogued motor size (across all brands) alongside
// the non-overloading trim it implies for this family - so picking "5.5kW /
// 4 pole" already comes with the impeller trim that keeps that motor safe.
// Brand-agnostic deliberately: the customer chooses a size first, and the
// brand dropdown is narrowed down afterwards to whichever brands actually
// stock that size (see getBrandsForMotorSize).
PS.getMotorSizeOptionsForFamily = function (familyCurves) {
  const options = [];

  PS.getAvailablePoles().forEach(poles => {
    const syncSpeed = PS.syncSpeedsByPoles[poles];

    let candidateCurves = familyCurves.filter(curve =>
      !PS.isVerticalMultistage(curve) &&
      Math.abs(PS.getBaseSpeedRpm(curve) - syncSpeed) < syncSpeed * 0.15
    );

    // A handful of single-curve families (e.g. SGH, fixed at 1750rpm) don't
    // land within the usual tolerance of any sync speed - with no other
    // curve to choose from, the nearest sync speed is unambiguous, so use it
    // rather than leaving the family with zero motor options entirely.
    if (!candidateCurves.length) {
      const nonVerticalCurves = familyCurves.filter(curve => !PS.isVerticalMultistage(curve));
      const distinctSpeeds = new Set(nonVerticalCurves.map(PS.getBaseSpeedRpm));

      if (distinctSpeeds.size === 1) {
        const onlySpeed = [...distinctSpeeds][0];
        const allPoles = PS.getAvailablePoles();
        const nearestPoles = allPoles.reduce((best, p) =>
          Math.abs(PS.syncSpeedsByPoles[p] - onlySpeed) < Math.abs(PS.syncSpeedsByPoles[best] - onlySpeed) ? p : best
        , allPoles[0]);

        if (nearestPoles === poles) {
          candidateCurves = nonVerticalCurves;
        }
      }
    }

    if (!candidateCurves.length) return;

    // Pick the nearest-to-synchronous *speed* first, then the largest
    // diameter within that one speed - some families also catalogue a curve
    // or two at an intermediate VFD speed inside the same tolerance window
    // (e.g. SFF has a single 228mm curve at 2700rpm alongside its full
    // 171-228mm spread at 2950rpm); picking by diameter across speeds first
    // can land on that thinner intermediate-speed set instead of the real
    // governing speed's full catalogued range.
    const nearestSpeed = candidateCurves.reduce((best, curve) => {
      const diff = Math.abs(PS.getBaseSpeedRpm(curve) - syncSpeed);
      const bestDiff = Math.abs(PS.getBaseSpeedRpm(best) - syncSpeed);
      return diff < bestDiff ? curve : best;
    }, candidateCurves[0]);

    const speedRpmForBucket = PS.getBaseSpeedRpm(nearestSpeed);

    const baseCurve = candidateCurves
      .filter(curve => PS.getBaseSpeedRpm(curve) === speedRpmForBucket)
      .sort((a, b) => PS.getImpellerDiameter(b) - PS.getImpellerDiameter(a))[0];

    if (!baseCurve) return;

    const baseTrimMm = PS.getImpellerDiameter(baseCurve);
    const speedRpm = PS.getBaseSpeedRpm(baseCurve);

    // Where the family has several explicitly catalogued diameters at this
    // speed (the normal case, e.g. SFF's 171-228mm spread), their own real
    // published shaft power is what's reliable - both for the floor (the
    // smallest catalogued diameter) and for the full-size power ceiling.
    // Surface-fitted families with only a single catalogued curve per speed
    // fall back to evaluating the continuous surface for the minimum trim
    // (diaMinMm) since there's no second catalogued row to derive it from.
    // Sourced from candidateCurves (already bucketed to this pole's sync
    // speed within tolerance) rather than re-filtering familyCurves by
    // exact rpm equality - some families (e.g. SKL) catalogue trims for the
    // same pole bucket at slightly different actual speeds (1450 vs 1500
    // rpm), and an exact-equality filter would silently drop those, leaving
    // only one trim and forcing a surface-fallback evaluation at the wrong
    // speed for the others (which inflated SKL's apparent power requirement
    // past every available motor's rating).
    const cataloguedTrims = candidateCurves
      .map(curve => ({ curve, diameterMm: PS.getImpellerDiameter(curve), powerKw: PS.getCurveMaxShaftPowerKw(curve) }))
      .filter(entry => entry.diameterMm > 0 && entry.powerKw > 0);
    const surface = PS.getSurfaceCoefficients(baseCurve);

    let minTrimMm;
    let basePowerKw;
    let minTrimPowerKw;

    if (cataloguedTrims.length > 1) {
      const smallest = cataloguedTrims.reduce((a, b) => (a.diameterMm <= b.diameterMm ? a : b));
      const largest = cataloguedTrims.reduce((a, b) => (a.diameterMm >= b.diameterMm ? a : b));
      minTrimMm = smallest.diameterMm;
      basePowerKw = largest.powerKw;
      minTrimPowerKw = smallest.powerKw;
    } else {
      minTrimMm = surface?.diaMinMm || Math.min(
        ...familyCurves
          .filter(curve => PS.getBaseSpeedRpm(curve) === speedRpm)
          .map(PS.getImpellerDiameter)
          .filter(value => Number.isFinite(value) && value > 0 && value <= baseTrimMm)
      );
      basePowerKw = PS.getMaxShaftPowerKwAtDiameter(baseCurve, surface, baseTrimMm, speedRpm);

      // The smallest usable impeller still demands this much shaft power -
      // any motor under that can't drive this family at all, regardless of
      // trim, so it's excluded outright rather than offered with a trim
      // clamped up to where it'd actually overload.
      minTrimPowerKw = PS.getMaxShaftPowerKwAtDiameter(baseCurve, surface, minTrimMm, speedRpm) || 0;
    }

    const sizesForPoles = PS.getAllMotorSizes()
      .filter(size => size.poles === poles && size.powerKw >= minTrimPowerKw)
      .sort((a, b) => a.powerKw - b.powerKw);

    // Anything beyond the smallest motor that already covers the
    // full-diameter curve without overloading is just oversized for this
    // impeller - offer up to two sizes above that as headroom (for
    // deliberate overspeed/future trim-up), not the rest of the catalog.
    const coveringIndex = basePowerKw
      ? sizesForPoles.findIndex(size => size.powerKw >= basePowerKw)
      : -1;

    const cappedSizes = coveringIndex === -1
      ? sizesForPoles
      : sizesForPoles.slice(0, coveringIndex + 3);

    cappedSizes.forEach(size => {
      // Default trim is sized against a *real* motor's full-load speed, not the
      // catalogued curve speed (see getModelMaxTrimForMotor). The size list is
      // brand-agnostic, so resolve a concrete motor for the currently selected
      // brand (falling back to any brand that stocks it) to read a real rpm; a
      // later brand change in the detail panel re-derives the trim for that
      // brand's own speed.
      const brand = PS.state.selectedMotorBrand || 'te3';
      const realMotor =
        PS.getMotorsForBrand(brand).find(motor => motor.poles === size.poles && Math.abs(motor.powerKw - size.powerKw) < 0.01) ||
        PS.getMotorsArray().find(motor => motor.poles === size.poles && Math.abs(motor.powerKw - size.powerKw) < 0.01) ||
        size;
      const defaultTrimMm = PS.getModelMaxTrimForMotor(baseCurve, familyCurves, realMotor);

      if (defaultTrimMm) {
        options.push({ motor: size, baseCurve, defaultTrimMm });
      }
    });
  });

  return options;
};

// Standard 50Hz synchronous speeds - the catalog curve's nominal speed sits
// just below these due to slip, so nearest-match is the reliable way to
// recover pole count from a curve's speedRpm.
PS.syncSpeedsByPoles = { 2: 3000, 4: 1500, 6: 1000, 8: 750 };

PS.inferPolesFromRpm = function (rpm) {
  const poleOptions = Object.keys(PS.syncSpeedsByPoles).map(Number);

  return poleOptions.reduce((best, poles) => {
    const diff = Math.abs(PS.syncSpeedsByPoles[poles] - rpm);
    const bestDiff = Math.abs(PS.syncSpeedsByPoles[best] - rpm);
    return diff < bestDiff ? poles : best;
  }, poleOptions[0]);
};

PS.getAvailablePolesForBrand = function (brand) {
  return [...new Set(PS.getMotorsForBrand(brand).map(motor => motor.poles))].sort((a, b) => a - b);
};

PS.selectMotorForLoad = function (brand, poles, requiredKw) {
  const candidates = PS.getMotorsForBrand(brand)
    .filter(motor => motor.poles === poles && motor.powerKw >= requiredKw)
    .sort((a, b) => a.powerKw - b.powerKw);

  return candidates[0] || null;
};

// Brand-agnostic equivalent of selectMotorForLoad - the smallest size that
// covers the load, across every brand. Used as a fallback so a brand-bound
// miss (e.g. SKL needing more than TE3's 185kW ceiling) doesn't leave the
// customer with no usable default motor at all.
PS.selectMotorSizeForLoad = function (poles, requiredKw) {
  const candidates = PS.getAllMotorSizes()
    .filter(size => size.poles === poles && size.powerKw >= requiredKw)
    .sort((a, b) => a.powerKw - b.powerKw);

  return candidates[0] || null;
};

// Southern Cross Vertical Multistage (family code PVMI, sold as "SBI") factory
// motors, keyed by TAPS unit code. Regenerate with
// `node "Build Scripts/build-vmi-unit-motors.js"` - source is that export's
// Mpower column, 50 Hz rows only (a 60 Hz row carries a ~1.7x larger motor;
// the script's header has the detail, including the ten unit codes whose 50 Hz
// motor TAPS doesn't record and which are therefore absent here).
//
// These pumps are NOT sized the way an ISO End Suction pump is. An ISO pump's
// impeller is trimmed to suit whichever motor the customer picks, so "smallest
// motor that covers the shaft power" is the right question. An SBI's impeller
// stack and its motor are one catalogued unit - PVMI15-07T IS a 5.5 kW pump -
// so the only right answer is the motor Southern Cross actually builds it with.
PS.vmiUnitMotorKw = {
  'PVMI1-02S': 0.37,
  'PVMI1-02T': 0.37,
  'PVMI1-03S': 0.37,
  'PVMI1-03T': 0.37,
  'PVMI1-04S': 0.37,
  'PVMI1-04T': 0.37,
  'PVMI1-05S': 0.37,
  'PVMI1-05T': 0.37,
  'PVMI1-06S': 0.37,
  'PVMI1-06T': 0.37,
  'PVMI1-07S': 0.37,
  'PVMI1-07T': 0.37,
  'PVMI1-08S': 0.55,
  'PVMI1-08T': 0.55,
  'PVMI1-09S': 0.55,
  'PVMI1-09T': 0.55,
  'PVMI1-10S': 0.55,
  'PVMI1-10T': 0.55,
  'PVMI1-11S': 0.55,
  'PVMI1-11T': 0.55,
  'PVMI1-12S': 0.75,
  'PVMI1-12T': 0.75,
  'PVMI1-13S': 0.75,
  'PVMI1-13T': 0.75,
  'PVMI1-15S': 0.75,
  'PVMI1-15T': 0.75,
  'PVMI1-17S': 1.1,
  'PVMI1-17T': 1.1,
  'PVMI1-19S': 1.1,
  'PVMI1-19T': 1.1,
  'PVMI1-21S': 1.1,
  'PVMI1-21T': 1.1,
  'PVMI1-23S': 1.1,
  'PVMI1-23T': 1.1,
  'PVMI1-25S': 1.5,
  'PVMI1-25T': 1.5,
  'PVMI1-27S': 1.5,
  'PVMI1-27T': 1.5,
  'PVMI1-30S': 1.5,
  'PVMI1-30T': 1.5,
  'PVMI1-33S': 2.2,
  'PVMI1-33T': 2.2,
  'PVMI1-36S': 2.2,
  'PVMI1-36T': 2.2,
  'PVMI10-01S': 0.37,
  'PVMI10-01T': 0.37,
  'PVMI10-02S': 0.75,
  'PVMI10-02T': 0.75,
  'PVMI10-03S': 1.1,
  'PVMI10-03T': 1.1,
  'PVMI10-04S': 1.5,
  'PVMI10-04T': 1.5,
  'PVMI10-05S': 2.2,
  'PVMI10-05T': 2.2,
  'PVMI10-06S': 2.2,
  'PVMI10-06T': 2.2,
  'PVMI10-07S': 3,
  'PVMI10-07T': 3,
  'PVMI10-08S': 3,
  'PVMI10-08T': 3,
  'PVMI10-09S': 3,
  'PVMI10-09T': 3,
  'PVMI10-10T': 4,
  'PVMI10-12T': 4,
  'PVMI10-14T': 5.5,
  'PVMI10-16T': 5.5,
  'PVMI10-18T': 7.5,
  'PVMI10-20T': 7.5,
  'PVMI10-22T': 7.5,
  'PVMI120-01T': 11,
  'PVMI120-02-1T': 18.5,
  'PVMI120-02T': 22,
  'PVMI120-03T': 30,
  'PVMI120-04-1T': 37,
  'PVMI120-05-1T': 45,
  'PVMI120-06-1T': 55,
  'PVMI120-07T': 75,
  'PVMI15-01S': 1.1,
  'PVMI15-01T': 1.1,
  'PVMI15-02S': 2.2,
  'PVMI15-02T': 2.2,
  'PVMI15-03S': 3,
  'PVMI15-03T': 3,
  'PVMI15-04T': 4,
  'PVMI15-05T': 4,
  'PVMI15-06T': 5.5,
  'PVMI15-07T': 5.5,
  'PVMI15-08T': 7.5,
  'PVMI15-09T': 7.5,
  'PVMI15-10T': 11,
  'PVMI15-12T': 11,
  'PVMI15-14T': 11,
  'PVMI15-17T': 15,
  'PVMI150-01-1T': 11,
  'PVMI150-01T': 15,
  'PVMI150-02-1T': 22,
  'PVMI150-03-2T': 30,
  'PVMI150-03T': 37,
  'PVMI150-04-1T': 45,
  'PVMI150-05-2T': 55,
  'PVMI150-06T': 75,
  'PVMI20-01S': 1.1,
  'PVMI20-01T': 1.1,
  'PVMI20-02S': 2.2,
  'PVMI20-02T': 2.2,
  'PVMI20-03T': 4,
  'PVMI20-04T': 5.5,
  'PVMI20-05T': 5.5,
  'PVMI20-06T': 7.5,
  'PVMI20-07T': 7.5,
  'PVMI20-08T': 11,
  'PVMI20-10T': 11,
  'PVMI20-12T': 15,
  'PVMI20-14T': 15,
  'PVMI20-17T': 18.5,
  'PVMI3-02S': 0.37,
  'PVMI3-02T': 0.37,
  'PVMI3-03S': 0.37,
  'PVMI3-03T': 0.37,
  'PVMI3-04S': 0.37,
  'PVMI3-04T': 0.37,
  'PVMI3-05S': 0.37,
  'PVMI3-05T': 0.37,
  'PVMI3-06S': 0.55,
  'PVMI3-06T': 0.55,
  'PVMI3-07S': 0.55,
  'PVMI3-07T': 0.55,
  'PVMI3-08S': 0.75,
  'PVMI3-08T': 0.75,
  'PVMI3-09S': 0.75,
  'PVMI3-09T': 0.75,
  'PVMI3-10S': 0.75,
  'PVMI3-10T': 0.75,
  'PVMI3-11S': 1.1,
  'PVMI3-11T': 1.1,
  'PVMI3-12S': 1.1,
  'PVMI3-12T': 1.1,
  'PVMI3-13S': 1.1,
  'PVMI3-13T': 1.1,
  'PVMI3-15S': 1.1,
  'PVMI3-15T': 1.1,
  'PVMI3-17S': 1.5,
  'PVMI3-17T': 1.5,
  'PVMI3-19S': 1.5,
  'PVMI3-19T': 1.5,
  'PVMI3-21S': 2.2,
  'PVMI3-21T': 2.2,
  'PVMI3-23S': 2.2,
  'PVMI3-23T': 2.2,
  'PVMI3-25S': 2.2,
  'PVMI3-25T': 2.2,
  'PVMI3-27S': 2.2,
  'PVMI3-27T': 2.2,
  'PVMI3-29S': 2.2,
  'PVMI3-29T': 2.2,
  'PVMI3-31S': 3,
  'PVMI3-31T': 3,
  'PVMI3-33S': 3,
  'PVMI3-33T': 3,
  'PVMI3-36S': 3,
  'PVMI3-36T': 3,
  'PVMI32-01-1S': 1.5,
  'PVMI32-01-1T': 1.5,
  'PVMI32-01S': 2.2,
  'PVMI32-01T': 2.2,
  'PVMI32-02-2S': 3,
  'PVMI32-02-2T': 3,
  'PVMI32-02T': 4,
  'PVMI32-03-2T': 5.5,
  'PVMI32-03T': 5.5,
  'PVMI32-04-2T': 7.5,
  'PVMI32-04T': 7.5,
  'PVMI32-05-2T': 11,
  'PVMI32-05T': 11,
  'PVMI32-06-2T': 11,
  'PVMI32-06T': 11,
  'PVMI32-07-2T': 15,
  'PVMI32-07T': 15,
  'PVMI32-08-2T': 15,
  'PVMI32-08T': 15,
  'PVMI32-09-2T': 18.5,
  'PVMI32-09T': 18.5,
  'PVMI32-10-2T': 18.5,
  'PVMI32-10T': 18.5,
  'PVMI32-11-2T': 22,
  'PVMI32-11T': 22,
  'PVMI32-12-2T': 22,
  'PVMI32-12T': 22,
  'PVMI32-13-2T': 30,
  'PVMI32-13T': 30,
  'PVMI32-14-2T': 30,
  'PVMI32-14T': 30,
  'PVMI45-01-1S': 3,
  'PVMI45-01-1T': 3,
  'PVMI45-01T': 4,
  'PVMI45-02-2T': 5.5,
  'PVMI45-02T': 7.5,
  'PVMI45-03-2T': 11,
  'PVMI45-03T': 11,
  'PVMI45-04-2T': 15,
  'PVMI45-04T': 15,
  'PVMI45-05-2T': 18.5,
  'PVMI45-05T': 18.5,
  'PVMI45-06-2T': 22,
  'PVMI45-06T': 22,
  'PVMI45-07-2T': 30,
  'PVMI45-07T': 30,
  'PVMI45-08-2T': 30,
  'PVMI45-08T': 30,
  'PVMI45-09-2T': 30,
  'PVMI45-09T': 37,
  'PVMI45-10-2T': 37,
  'PVMI45-10T': 37,
  'PVMI45-11-2T': 45,
  'PVMI45-11T': 45,
  'PVMI45-12-2T': 45,
  'PVMI45-12T': 45,
  'PVMI45-13T': 45,
  'PVMI5-02S': 0.37,
  'PVMI5-02T': 0.37,
  'PVMI5-03S': 0.55,
  'PVMI5-03T': 0.55,
  'PVMI5-04S': 0.55,
  'PVMI5-04T': 0.55,
  'PVMI5-05S': 0.75,
  'PVMI5-05T': 0.75,
  'PVMI5-06S': 1.1,
  'PVMI5-06T': 1.1,
  'PVMI5-07S': 1.1,
  'PVMI5-07T': 1.1,
  'PVMI5-08S': 1.1,
  'PVMI5-08T': 1.1,
  'PVMI5-09S': 1.5,
  'PVMI5-09T': 1.5,
  'PVMI5-10S': 1.5,
  'PVMI5-10T': 1.5,
  'PVMI5-11S': 2.2,
  'PVMI5-11T': 2.2,
  'PVMI5-12S': 2.2,
  'PVMI5-12T': 2.2,
  'PVMI5-13S': 2.2,
  'PVMI5-13T': 2.2,
  'PVMI5-14S': 2.2,
  'PVMI5-14T': 2.2,
  'PVMI5-15S': 2.2,
  'PVMI5-15T': 2.2,
  'PVMI5-16S': 2.2,
  'PVMI5-16T': 2.2,
  'PVMI5-18S': 3,
  'PVMI5-18T': 3,
  'PVMI5-20S': 3,
  'PVMI5-20T': 3,
  'PVMI5-22T': 4,
  'PVMI5-24T': 4,
  'PVMI5-26T': 4,
  'PVMI5-29T': 4,
  'PVMI5-32T': 5.5,
  'PVMI5-36T': 5.5,
  'PVMI64-01-1T': 4,
  'PVMI64-01T': 5.5,
  'PVMI64-02-1T': 11,
  'PVMI64-02-2T': 7.5,
  'PVMI64-02T': 11,
  'PVMI64-03-1T': 15,
  'PVMI64-03-2T': 15,
  'PVMI64-03T': 18.5,
  'PVMI64-04-1T': 22,
  'PVMI64-04-2T': 18.5,
  'PVMI64-04T': 22,
  'PVMI64-05-1T': 30,
  'PVMI64-05-2T': 30,
  'PVMI64-05T': 30,
  'PVMI64-06-1T': 37,
  'PVMI64-06-2T': 30,
  'PVMI64-06T': 37,
  'PVMI64-07-1T': 37,
  'PVMI64-07-2T': 37,
  'PVMI64-07T': 45,
  'PVMI64-08-1T': 45,
  'PVMI64-08-2T': 45,
  'PVMI90-01-1T': 5.5,
  'PVMI90-01T': 7.5,
  'PVMI90-02-2T': 11,
  'PVMI90-02T': 15,
  'PVMI90-03-2T': 18.5,
  'PVMI90-03T': 22,
  'PVMI90-04-2T': 30,
  'PVMI90-04T': 30,
  'PVMI90-05-2T': 37,
  'PVMI90-05T': 37,
  'PVMI90-06-2T': 45,
  'PVMI90-06T': 45
};

// The factory motor rating (kW) for a Vertical Multistage match, or null when
// the normal power-based sizing in getAutoSizedMotor should apply instead.
//
// Why this is needed at all: the published run-out shaft power of an SBI can
// sit slightly ABOVE its own nameplate motor - 47 of the 292 catalogued 50 Hz
// units do, PVMI15-07T being a 5.5 kW unit whose power curve peaks at 5.80 kW.
// Sizing those on shaft power pushes them to the next frame (5.5 -> 7.5 kW), a
// unit Southern Cross does not build. TAPS itself keeps the fixed motor and
// simply prints the run-out figure as "MAX POWER DRAW" (see
// PS.getMotorOverloadInfo, which still surfaces it here).
//
// This is what the unit SHIPS with, at any speed - it is never swapped for a
// bigger one behind the customer's back, because you cannot buy a PVMI10-09T
// with a bigger motor and quoting one would describe a unit Southern Cross
// doesn't build. Where more motor is genuinely needed (an overspeed, or a duty
// out past nameplate), PS.getMotorSizingRequirementKw says so and the
// adjustment panel tells the customer to size it up themselves.
PS.getVerticalUnitMotorKw = function (match) {
  if (!match || !PS.isVerticalMultistage(match)) return null;

  const curve = match.curve || match;
  const unitCode = String(curve.unitCode || '').trim().toUpperCase();
  const factoryKw = PS.vmiUnitMotorKw[unitCode];

  return Number.isFinite(factoryKw) ? factoryKw : null;
};

// Is this Vertical Multistage match being run above mains? The factory motor is
// a 50 Hz motor sized for the unit at mains, run-out excursion included - past
// that, shaft power goes as speed^3 and the pairing stops describing what the
// pump is being asked to do.
//
// "Above mains" is measured as shaft rpm past the pole count's SYNCHRONOUS
// speed, not as frequency past 50 Hz, because a real motor never reaches
// synchronous speed on mains: a 2-pole runs 2850-2970 rpm, so the search's own
// idealised 3000 rpm candidates read as 50.5-52.6 Hz once
// PS.refineMatchWithEffectiveMotor recomputes frequency against the real
// motor's nameplate. Testing frequency would flag those ordinary at-mains
// matches as over-driven. Synchronous speed is the honest ceiling - no 50 Hz
// supply can push the shaft past it - and it needs no motor to evaluate, which
// matters because the motor is resolved through this function.
PS.isVerticalUnitOverspeed = function (match) {
  const syncRpm = PS.syncSpeedsByPoles[PS.getEffectiveMotorPoles(match)];
  const selectedRpm = match?.selectedSpeedRpm;

  if (syncRpm && Number.isFinite(selectedRpm)) return selectedRpm > syncRpm * 1.02;

  // No speed on the match at all - fall back to the frequency it was asked for.
  const mainsHz = PS.unitSettings.frequency || 50;

  return !!(Number.isFinite(match?.selectedFrequencyHz) && match.selectedFrequencyHz > mainsHz * 1.02);
};

// Can this pump actually be supplied on a single-phase motor? Returns
// { ok, reason } - the reason is customer-facing, so it names what blocks it
// rather than just refusing.
//
// Four different rules, because "single phase" means something different per
// range:
//  - Any variable-speed selection is three phase, full stop. A single-phase
//    motor cannot be speed-varied; variable speed on a single-phase supply
//    means a 230 V three-phase motor on a single-phase-input drive, which is
//    a different motor than anything catalogued here.
//  - A fixed-motor pump (Onga, Jung) publishes its own phase - it comes with
//    its motor, so there is nothing to choose.
//  - A Vertical Multistage unit needs Southern Cross to catalogue an S build
//    for that exact stack. This is stricter than the kW rule and deliberately
//    so: twelve stacks sit at exactly 4.0 kW with no S build, and the tool
//    stays true to the catalogue (and therefore to TAPS) rather than inferring
//    that a 4 kW single-phase motor would fit.
//  - Everything else is a bare pump with a motor bolted on, so 43 South's own
//    limit on what they will supply single phase is the whole test.
PS.getSinglePhaseAvailability = function (match) {
  if (!match) return { ok: false, reason: 'no pump selected' };

  if (match.controlType === 'vsd' || match.controlType === 'speedRange') {
    return { ok: false, reason: 'a variable-speed selection needs a three-phase motor - a single-phase motor cannot be speed-varied' };
  }

  const curve = match.curve || match;

  if (match.fixedPump || curve.fixedPump) {
    const phases = curve.motor?.phases;
    return phases === 1
      ? { ok: true }
      : { ok: false, reason: 'this pump comes with its own ' + (phases || 'three') + '-phase motor' };
  }

  if (PS.isVerticalMultistage(match)) {
    return PS.hasVerticalBuildForPhase(curve, 1)
      ? { ok: true }
      : { ok: false, reason: 'Southern Cross does not catalogue a single-phase build of this stack' };
  }

  const autoMotor = PS.getAutoSizedMotor(match).motor;
  const motorKw = autoMotor?.powerKw;

  if (!Number.isFinite(motorKw) || motorKw > PS.SINGLE_PHASE_MAX_KW) {
    return {
      ok: false,
      reason: 'needs a ' + (Number.isFinite(motorKw) ? PS.formatNumber(motorKw, 2) + ' kW' : 'larger')
        + ' motor, over the ' + PS.SINGLE_PHASE_MAX_KW + ' kW single-phase limit'
    };
  }

  // Inside the commercial limit, but the single-phase range still has to stock
  // the size - see PS.hasSinglePhaseMotorForLoad.
  if (!PS.hasSinglePhaseMotorForLoad(autoMotor.poles, motorKw)) {
    return {
      ok: false,
      reason: 'no single-phase motor is catalogued at ' + PS.formatNumber(motorKw, 2)
        + ' kW / ' + autoMotor.poles + ' pole'
    };
  }

  return { ok: true };
};

// The phase actually in force for this match. PS.state.selectedMotorPhase is
// the customer's pick and is sticky global state (like selectedMotorKw), so a
// single-phase pick left over from a different pump must never silently apply
// to one that can't take it - hence the availability check on every read.
PS.getEffectiveMotorPhase = function (match) {
  return (PS.state.selectedMotorPhase === 1 && PS.getSinglePhaseAvailability(match).ok) ? 1 : 3;
};

// A single-phase motor runs at whatever speed the mains gives it, so nothing
// that varies speed applies to it: the frequency stepper and the extra-speed
// curve overlays are both hidden. Call sites read this rather than testing the
// phase directly, so the rule lives in one place.
//
// The exception is a pump that arrives with its own integrated drive - Onga's
// Intellimaster range is single-phase AND speed-adjustable (25-50 Hz), because
// the inverter is inside the pump. That is the same 230V-three-phase-motor-on-
// a-single-phase-input answer the warning elsewhere points at, solved by the
// manufacturer and sold as one unit. The rule below is about a single-phase
// motor WE supply separately and bolt on, which has no drive in front of it.
PS.supportsSpeedAdjustment = function (match) {
  if (match?.speedAdjustable) return true;

  return PS.getEffectiveMotorPhase(match) === 3;
};

// The highest shaft speed (rpm) the adjustment panel will let this match be
// driven at, or null for no ceiling. Overspeeding a Vertical Multistage unit
// past mains needs a bigger motor than the one it is built with, so it is an
// Advanced-mode decision: Basic caps the speed control at mains and the
// customer reaches for more stages when they want more head, exactly as they
// would off the catalogue. Advanced lifts the cap and warns instead (see
// PS.getMotorSizingRequirementKw and the adjustment panel's undersized
// warning).
//
// Expressed in rpm against synchronous speed, deliberately matching
// PS.isVerticalUnitOverspeed's ceiling exactly - a Hz-based cap of 50 would
// clamp the search's own at-mains VSD matches, which sit at a synchronous
// 3000 rpm and therefore read as ~51.3 Hz against a real motor's nameplate.
PS.getSpeedAdjustmentCeilingRpm = function (match) {
  if (PS.state.selectionMode === 'advanced') return null;
  if (PS.getVerticalUnitMotorKw(match) === null) return null;

  return PS.syncSpeedsByPoles[PS.getEffectiveMotorPoles(match)] || null;
};

// The kW a motor has to cover for this match - the single source of truth
// behind both the auto-sized motor and the adjustment panel's undersized
// warning, so the two can't disagree about what "big enough" means.
PS.getMotorSizingRequirementKw = function (match) {
  const factoryKw = PS.getVerticalUnitMotorKw(match);

  if (factoryKw !== null) {
    // Run above mains, the customer is asking for more than the factory
    // pairing covers, so the real power demand at that speed is the
    // requirement - that gap is exactly what the adjustment panel's warning
    // reports, and what the customer then sizes the motor up to.
    if (PS.isVerticalUnitOverspeed(match)) {
      return Math.max(factoryKw, PS.getPowerBasedMotorRequirementKw(match));
    }

    // At or below mains the factory pairing IS the requirement: Southern Cross
    // builds the unit that way, including the run-out excursion past nameplate
    // that 47 of the 292 catalogued units have. A duty sitting past nameplate
    // is still a real overload though, so it still shows through.
    return Math.max(factoryKw, match?.power || 0);
  }

  return PS.getPowerBasedMotorRequirementKw(match);
};

// The shaft power a motor has to cover when nothing dictates its size - every
// pump except a Vertical Multistage unit at or below mains.
PS.getPowerBasedMotorRequirementKw = function (match) {
  // Motor sizing is gated on the selection mode (PS.state.selectionMode):
  //  - Basic (default, customer-facing): size against the worst-case (run-out)
  //    shaft power so the motor can NEVER overload anywhere on the curve, even
  //    if the pump is run out to the right of its duty. Safer, one size larger.
  //  - Advanced (staff / TAPS-replacement): size to the DUTY-point shaft power,
  //    exactly as a TAPS operator does - TAPS picks the next motor up from the
  //    duty power and merely *warns* if the run-out draw exceeds it (its sheet
  //    shows an 11 kW motor with "MAX POWER DRAW = 11.83 kW"). This reproduces
  //    TAPS's motor selection; the run-out overload is surfaced as a warning
  //    (see PS.getMotorOverloadInfo) rather than avoided by up-sizing.
  const dutyKw = match?.power || 0;
  const worstCaseKw = PS.getMatchWorstCasePowerKw(match) || dutyKw;

  return PS.state.selectionMode === 'advanced'
    ? (dutyKw || worstCaseKw)
    : worstCaseKw;
};

// Motor at one specific catalogued rating, for a pump whose motor is fixed by
// the catalogue rather than chosen to cover a load (see getVerticalUnitMotorKw).
// Matched by value with a tolerance, because a few catalogue entries carry
// float32 rounding from the TAPS export (0.55 kW is stored as 0.55000001) that
// an exact === would miss.
PS.selectMotorAtSize = function (brand, poles, powerKw) {
  return PS.getMotorsForBrand(brand)
    .find(motor => motor.poles === poles && Math.abs(motor.powerKw - powerKw) < 0.01) || null;
};

// Auto/non-overloading motor for this match, ignoring any manual brand/kW
// override sitting in state - the "what should this pump get by default"
// answer. Split out from PS.getEffectiveMotor (below) so search-time logic
// that must stay independent of whatever a customer manually picked while
// looking at a *different* pump (PS.state.selectedMotorKw is sticky global
// state - see PS.getEffectiveMotor) can call this instead of accidentally
// picking up a leftover undersized selection.
PS.getAutoSizedMotor = function (match) {
  const poles = PS.getEffectiveMotorPoles(match);
  // What the motor has to cover - the catalogued factory rating for a Vertical
  // Multistage unit, otherwise the run-out (Basic) or duty (Advanced) shaft
  // power. See PS.getMotorSizingRequirementKw.
  const requiredKw = PS.getMotorSizingRequirementKw(match);
  // A Vertical Multistage unit's motor is a specific catalogued size, not the
  // smallest that covers a load, so it's matched by value - "next size up"
  // would be the wrong question and, for the 47 units whose run-out power sits
  // just above their own nameplate, the wrong answer.
  const factoryKw = PS.getVerticalUnitMotorKw(match);
  const pickMotor = motorBrand => (factoryKw !== null
    ? (PS.selectMotorAtSize(motorBrand, poles, factoryKw) || PS.selectMotorForLoad(motorBrand, poles, requiredKw))
    : PS.selectMotorForLoad(motorBrand, poles, requiredKw));

  let brand = PS.state.selectedMotorBrand || 'te3';
  let defaultMotor = pickMotor(brand);

  // The currently selected brand can't supply anything big enough for this
  // duty - rather than falling through to an undefined/undersized "default",
  // switch to whichever brand stocks the smallest size that actually covers
  // it (only when the customer hasn't explicitly picked a kW themselves).
  if (!defaultMotor && PS.state.selectedMotorKw == null) {
    const genericSize = PS.selectMotorSizeForLoad(poles, requiredKw);

    if (genericSize) {
      const coveringBrands = PS.getBrandsForMotorSize(poles, genericSize.powerKw);

      if (coveringBrands.length && !coveringBrands.some(({ value }) => value === brand)) {
        brand = coveringBrands[0].value;
        PS.state.selectedMotorBrand = brand;
        defaultMotor = pickMotor(brand);
      }
    }
  }

  return { motor: defaultMotor || { poles }, poles, brand, requiredKw };
};

// Resolves the actual motor object behind the current selection: an explicit
// brand/pole/kW pick if the user has made one, otherwise the auto "next size
// up" motor for whatever poles the curve implies. Returning the real motor
// (not just its pole count) is what lets Hz<->RPM conversion use its actual
// nameplate speed instead of an idealised synchronous speed.
PS.getEffectiveMotor = function (match) {
  const { motor: defaultMotor, poles, brand } = PS.getAutoSizedMotor(match);

  const selectedKw = PS.state.selectedMotorKw ?? defaultMotor?.powerKw ?? null;

  if (selectedKw === null) {
    return defaultMotor || { poles };
  }

  const selectedMotor = PS.getMotorsForBrand(brand).find(motor =>
    motor.poles === poles && Math.abs(motor.powerKw - selectedKw) < 0.01
  );

  // Honor an explicit manual pick even when it's smaller than the pump's own
  // worst-case required power - Southern Cross VSD customers can knowingly
  // choose a slightly undersized motor (e.g. a duty that rarely reaches
  // run-out), and every consumer of this motor (Hz/RPM math, bearing frame,
  // CAD, GA dimensions, the Power-chart overload indicator) should reflect
  // the real motor actually selected rather than a silently-substituted
  // bigger one. Only fall back to the auto-sized default when selectedKw is
  // sticky global state left over from a different pump/duty point (e.g. a
  // Search by Model pick, or a restored share-link) that doesn't resolve to
  // a real motor at all for this brand/pole combination.
  if (selectedMotor) {
    return selectedMotor;
  }

  return defaultMotor || { poles };
};

// PS.getEffectiveMotor always returns a real (or stub) motor object, even
// for fixed pumps that have no 43 South motor selection at all - internal
// calculations (Hz<->RPM conversion, refineMatchWithEffectiveMotor, etc.)
// rely on that never being null. This wrapper is for customer-facing
// display only (detail summary, printable sheet): Onga/other fixed pumps
// ship with their own integrated motor unrelated to the TE3/Teco/WEG
// database, so the auto-selected fallback motor must never be shown to the
// customer as if it were a real recommendation for a pump whose motor
// can't actually be changed.
PS.getDisplayMotor = function (match) {
  if (match?.fixedPump || match?.curve?.fixedPump) return null;
  return PS.getEffectiveMotor(match);
};

// Nameplate motor data for a fixed-motor pump (Onga/Jung etc), read straight
// off the pump's own JSON `motor` object rather than the TE3/Teco/WEG
// swappable-motor database (which never applies to these integrated-motor
// pumps). Display only - used to show the pump's own rated power/phase in the
// detail summary and printable sheet. Returns null when there's no usable
// nameplate rating, so callers fall back to "-" exactly as before.
PS.getFixedPumpNameplate = function (match) {
  if (!(match?.fixedPump || match?.curve?.fixedPump)) return null;
  const motor = match?.curve?.motor;
  if (!motor || !Number.isFinite(motor.nominalPowerKw)) return null;
  return {
    powerKw: motor.nominalPowerKw,
    phases: Number.isFinite(motor.phases) ? motor.phases : null,
    voltageV: Number.isFinite(motor.voltageV) ? motor.voltageV : null
  };
};

// Human-readable one-line rating for a fixed pump's integrated motor, e.g.
// "0.75 kW (1-phase, 230 V)". Returns null when no nameplate data exists.
PS.getFixedPumpMotorText = function (match) {
  const plate = PS.getFixedPumpNameplate(match);
  if (!plate) return null;
  const parts = [];
  if (plate.phases) parts.push(`${plate.phases}-phase`);
  if (plate.voltageV) parts.push(`${PS.formatNumber(plate.voltageV, 0)} V`);
  const suffix = parts.length ? ` (${parts.join(', ')})` : '';
  return `${PS.formatNumber(plate.powerKw, 2)} kW${suffix}`;
};

// Rated power of the currently selected motor, for the VSD Power-chart
// overload indicator (pump-selector-charts.js) - deliberately restricted to
// Southern Cross's own trimmable/VSD path, the only range today where a
// customer chooses a 43 South motor at all (Onga and every other fixed-motor
// manufacturer have no Motor Selection card, per PS.getDisplayMotor above).
// Returns null for every other case so the chart draws exactly as before.
PS.getSelectedMotorRatedKw = function (match) {
  if (!match || match.controlType !== 'vsd') return null;
  if (match.fixedPump || match.curve?.fixedPump) return null;
  if (match.curve?.manufacturer !== 'Southern Cross') return null;

  const motor = PS.getEffectiveMotor(match);
  return motor?.powerKw || null;
};

// Motor efficiency at a given shaft load, exactly as TAPS computes it. TAPS
// does not interpolate the catalogued quarter/half/three-quarter/full-load
// efficiency points - it evaluates a quadratic fit for the motor's electrical
// INPUT power as a function of load, then divides (GpSelect2):
//     ML = PV / Mpower                       (shaft power / motor rating)
//     MP = MC0 + MC1 * ML + MC2 * ML ^ 2     (input power, kW)
//     Moteff = PV * 100 / MP
// MC0/MC1/MC2 come from TAPS's own Motor Data table (imported by
// Build Scripts/sync-motor-data-from-taps.js). Verified against a real curve
// sheet: a TECO 2.2 kW 6-pole at 1.56 kW shaft gives 84.91% here, and TAPS
// prints 84.9%.
//
// Falls back to the old piecewise-linear interpolation for any motor without
// the coefficients (nothing in the current catalogue, but third-party motors
// added later may lack them).
PS.getMotorEfficiencyPct = function (motor, shaftPowerKw) {
  if (!motor || !Number.isFinite(shaftPowerKw) || shaftPowerKw <= 0) return null;
  if (!Number.isFinite(motor.powerKw) || motor.powerKw <= 0) return null;

  const { inputPowerMc0: mc0, inputPowerMc1: mc1, inputPowerMc2: mc2 } = motor;
  if (Number.isFinite(mc0) && Number.isFinite(mc1) && Number.isFinite(mc2)) {
    const load = shaftPowerKw / motor.powerKw;
    const inputKw = mc0 + mc1 * load + mc2 * load * load;
    if (Number.isFinite(inputKw) && inputKw > 0) {
      const eff = shaftPowerKw * 100 / inputKw;
      if (eff > 0 && eff <= 100) return eff;
    }
  }

  return PS.interpolateMotorEfficiency(motor, shaftPowerKw / motor.powerKw);
};

// Piecewise-linear interpolation across TAPS's quarter/half/three-quarter/
// full-load efficiency points. A 0 value means TAPS has no reading at that
// load point (common for QLE) - fall back to the next point up rather than
// interpolating against a false zero.
PS.interpolateMotorEfficiency = function (motor, loadFraction) {
  if (!motor) return null;

  const points = [
    { load: 0.25, eff: motor.quarterLoadEffPct },
    { load: 0.5, eff: motor.halfLoadEffPct },
    { load: 0.75, eff: motor.threeQuarterLoadEffPct },
    { load: 1.0, eff: motor.fullLoadEffPct }
  ].filter(p => p.eff > 0);

  if (!points.length) return null;

  const clamped = Math.max(points[0].load, Math.min(points[points.length - 1].load, loadFraction));

  for (let i = 0; i < points.length - 1; i += 1) {
    const a = points[i];
    const b = points[i + 1];

    if (clamped >= a.load && clamped <= b.load) {
      const ratio = (clamped - a.load) / (b.load - a.load);
      return a.eff + (b.eff - a.eff) * ratio;
    }
  }

  return points[points.length - 1].eff;
};

// Unit sound-pressure level (dBA), matching TAPS exactly. A motor's catalogued
// splDba is the bare-motor figure; TAPS reports the assembled *unit* noise,
// which adds a fixed allowance for the second co-located sound source (the pump
// end) plus a VFD allowance. From GpSelect2 (the "NOISE LEVEL" report line):
//   UnitSPL = SPL + 3   for 2 & 4 pole units   (2x sound power)
//   UnitSPL = SPL + 6   for 6 & 8 pole units   (4x sound power)
//   UnitSPL = UnitSPL + 2   for VFD units       (controlType 'vsd')
// Returns null when the motor has no catalogued SPL (fixed-motor Onga/Jung
// pumps have no swappable-database motor, so this never applies to them).
PS.getUnitNoiseDba = function (match, motor) {
  if (!motor || !Number.isFinite(motor.splDba)) return null;

  let spl = motor.splDba;
  if (motor.poles === 2 || motor.poles === 4) spl += 3;
  else if (motor.poles === 6 || motor.poles === 8) spl += 6;
  if (match?.controlType === 'vsd') spl += 2;

  return spl;
};

// The pump's real operating speed. For a mains (fixed-speed) induction motor
// this is load-dependent: the motor slips less than its full-load nameplate
// figure when it isn't fully loaded, so it turns faster - closer to
// synchronous speed. TAPS models this linearly (GpSelect2: Pspeed = N1 + S0,
// i.e. Pspeed = SP0 - PV*(SP0-SP1)/Mpower):
//   speed = synchronous - (shaftPower/ratedPower) * (synchronous - nameplate)
// e.g. an 11 kW 2-pole (3000 sync, 2935 nameplate) pulling 9.7 kW turns at
// 3000 - (9.7/11)*(3000-2935) = 2943 rpm, not the flat 2935 nameplate.
//
// Scoped to the Southern Cross swappable-motor fixed/fixed60 path only:
//  - vsd/speedRange already solve an explicit physical shaft speed;
//  - Onga/Jung fixed-motor pumps run their own integrated motor, not the
//    TE3/Teco/WEG database this models.
// Returns null in those cases so callers fall back to match.selectedSpeedRpm.
// Display-only: this does not change the head/power/efficiency/NPSHr the curve
// was evaluated at (Southern Cross's protected values are untouched) - it
// reports the operating speed the way TAPS does.
PS.getOperatingSpeedRpm = function (match, motor) {
  if (!match || (match.controlType !== 'fixed' && match.controlType !== 'fixed60')) return null;
  if (match.fixedPump || match.curve?.fixedPump) return null;
  if ((match.curve?.manufacturer || match.manufacturer) !== 'Southern Cross') return null;
  if (!motor || !Number.isFinite(motor.fullLoadRpm) || !Number.isFinite(motor.powerKw) || motor.powerKw <= 0) return null;
  if (!Number.isFinite(match.power)) return null;

  const syncRpm = PS.syncSpeedsByPoles[motor.poles];
  if (!syncRpm) return null;

  const loadFraction = match.power / motor.powerKw;
  return syncRpm - loadFraction * (syncRpm - motor.fullLoadRpm);
};

PS.getMatchWorstCasePowerKw = function (match) {
  if (!match) return null;

  const trimMm = match.selectedTrimMm || PS.getImpellerDiameter(match.curve);
  const speedRpm = match.selectedSpeedRpm;

  return PS.getMaxShaftPowerKwAtDiameter(match.curve, match.surface, trimMm, speedRpm) ?? match.power ?? null;
};

// Run-out overload state for the selected motor, mirroring TAPS's "MAX POWER
// DRAW" figure and its overload warning. In Advanced mode the motor is sized to
// the duty point (like TAPS), so a pump whose power rises to the right can draw
// more than the motor's rating at run-out - TAPS shows the max draw and flags
// it in red. Returns the worst-case (run-out) draw, the motor rating, and
// whether the draw exceeds it. Null for fixed-motor pumps (no swappable motor)
// and when no real motor/worst-case is resolvable.
// Maximum shaft power the pump can draw at run-out, computed the TAPS way. At
// run-out the motor is more heavily loaded, slips further and turns slower, so
// the pump draws less than a flat-nameplate-speed estimate. Iterate the run-out
// point's own load-adjusted speed to convergence (mirrors PS.solveLoadAdjusted-
// Duty, but at the peak-power point instead of the duty). Needs a real motor;
// falls back to the flat worst-case otherwise. Display/warning use only - motor
// sizing keeps the conservative flat estimate (PS.getMatchWorstCasePowerKw).
PS.getLoadAdjustedMaxDrawKw = function (match, motor) {
  // Vertical Multistage is deliberately excluded from the surface path, the
  // same way PS.evaluateAdjustedCurve excludes it (which is why a vertical
  // match carries surface: null). Its family surface is fitted per pump size,
  // not per impeller stack, so evaluating it for one stage count returns a
  // figure belonging to no real unit - a 5.5 kW SBI15-7 read as drawing
  // 0.82 kW at run-out instead of its real 5.80 kW. With no per-stack surface
  // to iterate, the flat worst case below is the honest answer.
  const surface = match && !PS.isVerticalMultistage(match) && PS.getSurfaceCoefficients(match.curve);
  const syncRpm = PS.syncSpeedsByPoles[motor?.poles];
  const nameplateRpm = motor?.fullLoadRpm;
  const ratedKw = motor?.powerKw;
  const trimMm = match?.selectedTrimMm;
  if (!surface || !syncRpm || !Number.isFinite(nameplateRpm) || !Number.isFinite(ratedKw) || ratedKw <= 0 || !Number.isFinite(trimMm)) {
    return PS.getMatchWorstCasePowerKw(match);
  }

  let speedRpm = match.selectedSpeedRpm || nameplateRpm;
  let maxKw = null;
  for (let i = 0; i < 25; i += 1) {
    maxKw = PS.getMaxShaftPowerKwAtDiameter(match.curve, surface, trimMm, speedRpm);
    if (!Number.isFinite(maxKw)) return PS.getMatchWorstCasePowerKw(match);
    const next = syncRpm - maxKw * (syncRpm - nameplateRpm) / ratedKw;
    if (!Number.isFinite(next) || Math.abs(next - speedRpm) < 0.02) break;
    speedRpm = next;
  }
  return maxKw;
};

PS.getMotorOverloadInfo = function (match, motor) {
  if (!match || match.fixedPump || match.curve?.fixedPump) return null;
  if (!motor || !Number.isFinite(motor.powerKw) || motor.powerKw <= 0) return null;

  const maxPowerDrawKw = PS.getLoadAdjustedMaxDrawKw(match, motor);
  if (!Number.isFinite(maxPowerDrawKw)) return null;

  return {
    maxPowerDrawKw,
    motorRatedKw: motor.powerKw,
    overloaded: maxPowerDrawKw > motor.powerKw + 1e-6
  };
};

// "Search by pump model" motor sizing, factoring the *real* selected motor's
// full-load speed - not the catalogued curve speed or an idealised
// synchronous speed. A pump driven at mains frequency turns at its motor's
// nameplate rpm, and that rpm differs by brand (e.g. a 2-pole 30kW runs at
// 2955rpm on a TECO TE3 but 2970rpm on a WEG W22), so the worst-case
// (run-out) shaft power - and therefore whether a given motor is big enough
// for a given impeller trim - is genuinely motor-specific. Both helpers below
// evaluate the family's real (Q,D,N) power surface at the motor's own rpm.
//
// These deliberately use the continuous surface (not the discrete catalogued
// trims) because the impeller can be trimmed to any diameter, so the answer
// is the true largest trim the motor supports, not just the nearest tested
// one. Surface-fitted families only (every Southern Cross ISO/Vertical range
// has one); for the handful of no-surface OEM ranges getMaxShaftPowerKwAtDiameter
// falls back to diameter-only affinity scaling, which can't factor rpm - the
// same limitation those ranges already had, so no behaviour change there.

// Largest impeller trim (mm) whose run-out shaft power at this motor's real
// full-load speed still fits the motor, bounded below by the family's minimum
// catalogued/surface diameter. Returns the full diameter when even that fits.
PS.getModelMaxTrimForMotor = function (baseCurve, familyCurves, motor) {
  if (!baseCurve || !motor || PS.isVerticalMultistage(baseCurve)) return null;

  const baseTrimMm = PS.getImpellerDiameter(baseCurve);
  if (!baseTrimMm) return null;

  const surface = PS.getSurfaceCoefficients(baseCurve);
  const speedRpm = PS.getMotorRatedRpmAt50Hz(motor);
  const motorKw = motor.powerKw;

  if (!speedRpm || !Number.isFinite(motorKw)) return baseTrimMm;

  const baseSpeedRpm = PS.getBaseSpeedRpm(baseCurve);
  const catalogued = (familyCurves || [])
    .filter(curve => !PS.isVerticalMultistage(curve) && PS.getBaseSpeedRpm(curve) === baseSpeedRpm)
    .map(PS.getImpellerDiameter)
    .filter(value => Number.isFinite(value) && value > 0 && value <= baseTrimMm);
  const floor = surface?.diaMinMm || (catalogued.length ? Math.min(...catalogued) : 1);

  const powerAtDiameter = diameterMm => PS.getMaxShaftPowerKwAtDiameter(baseCurve, surface, diameterMm, speedRpm);

  const basePower = powerAtDiameter(baseTrimMm);
  if (basePower !== null && basePower <= motorKw) return baseTrimMm;

  let lo = floor;
  let hi = baseTrimMm;
  let best = null;

  for (let i = 0; i < 30; i += 1) {
    const mid = (lo + hi) / 2;
    const power = powerAtDiameter(mid);

    if (power !== null && power <= motorKw) {
      best = mid;
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return best !== null ? Math.max(floor, Math.round(best)) : Math.round(floor);
};

// Smallest real motor (of the given pole count, preferring the given brand)
// whose rating covers the pump's run-out shaft power at that motor's own
// full-load speed for the requested trim. This is the "auto-size the motor to
// the trim" selection: e.g. a 217mm trim that draws 30.4kW at 2955rpm gets a
// 37kW motor, not the 30kW that only fits up to 216mm. Falls back across all
// brands if the preferred brand stocks nothing large enough, and finally to
// the largest available size, so it never returns null for a real trim.
PS.selectMotorForModelTrim = function (baseCurve, familyCurves, trimMm, poles, brand) {
  if (!baseCurve || PS.isVerticalMultistage(baseCurve)) return null;

  const surface = PS.getSurfaceCoefficients(baseCurve);
  const fits = motor => {
    const power = PS.getMaxShaftPowerKwAtDiameter(baseCurve, surface, trimMm, PS.getMotorRatedRpmAt50Hz(motor));
    return power === null || power <= motor.powerKw;
  };

  const ofBrand = (brand ? PS.getMotorsForBrand(brand) : [])
    .filter(motor => motor.poles === poles)
    .sort((a, b) => a.powerKw - b.powerKw);
  const brandFit = ofBrand.find(fits);
  if (brandFit) return brandFit;

  const anyBrand = PS.getMotorsArray()
    .filter(motor => motor.poles === poles)
    .sort((a, b) => a.powerKw - b.powerKw);
  const anyFit = anyBrand.find(fits);
  if (anyFit) return anyFit;

  // Nothing covers it (trim past the largest motor's rating) - return the
  // biggest available so the caller still has a real motor to fall back on;
  // the existing undersized-motor warning then correctly flags it.
  return (ofBrand.length ? ofBrand : anyBrand).slice(-1)[0] || null;
};

PS.calculateUnitEfficiency = function (match, brand) {
  if (!match || !Number.isFinite(match.power) || match.power <= 0) return null;

  // Same rationale as PS.getEffectiveMotorPoles (pump-selector-calcs.js) -
  // pole count belongs to the curve's rated speed, not wherever a VSD has
  // the shaft running right now.
  const poles = PS.inferPolesFromRpm(match.baseSpeedRpm ?? match.selectedSpeedRpm);
  const motor = PS.selectMotorForLoad(brand, poles, PS.getMatchWorstCasePowerKw(match) || match.power);

  return PS.calculateUnitEfficiencyForMotor(match, motor);
};

// Same calculation as above, but against a specific motor object rather than
// re-deriving brand/poles and auto-selecting - used once the user (or the
// auto-default) has actually settled on a motor, so the displayed unit
// efficiency reflects what's really selected rather than re-guessing it.
PS.calculateUnitEfficiencyForMotor = function (match, motor) {
  if (!match || !Number.isFinite(match.power) || match.power <= 0 || !motor || !motor.powerKw) {
    return null;
  }

  const loadFraction = match.power / motor.powerKw;
  const motorEffPct = PS.getMotorEfficiencyPct(motor, match.power);

  if (motorEffPct === null || !Number.isFinite(match.efficiency)) {
    return { motor, poles: motor.poles, loadFraction, motorEffPct, unitEfficiencyPct: null };
  }

  return {
    motor,
    poles: motor.poles,
    loadFraction,
    motorEffPct,
    unitEfficiencyPct: (match.efficiency / 100) * (motorEffPct / 100) * 100
  };
};

PS.getPuCode = function (motorKw, poles) {
  const rows = Array.isArray(PS.motorData?.puCodes) ? PS.motorData.puCodes : [];
  const row = rows.find(r => Math.abs(r.powerKw - motorKw) < 0.01);

  if (!row) return null;

  const code = row[`code${poles}`];
  return code && code !== '-' ? code : null;
};

// TAPS's pump family naming keeps the obsolete-replacement suffix ("nr") on
// curve/family names, but the underlying casing dimensions never changed -
// the dimension tables are still keyed by the original short pump code.
PS.getDimensionPumpCode = function (familyName) {
  return String(familyName || '').replace(/nr$/, '');
};

PS.findDimensionRow = function (match, mountingType, motor) {
  const pumpCode = PS.getDimensionPumpCode(match.family);
  const mountingConfig = PS.dimensionData?.mountingTypes?.[mountingType];

  if (!mountingConfig) return null;

  // PWT shares the Iso-Pro table dimensionally (long coupled, oil bath
  // bearing housing) - only its GA drawing image/title differ.
  if (mountingType === 'isoPro' || mountingType === 'pwt') {
    return (PS.dimensionData.isoDimensions || []).find(row => row.Pump === pumpCode) || null;
  }

  if (!motor) return null;

  const sourceKeys = mountingConfig.sources;

  return (PS.dimensionData.dimensions || []).find(row =>
    sourceKeys.includes(row._source) &&
    row.Pump === pumpCode &&
    Number(row.Mpoles) === motor.poles &&
    Math.abs(Number(row.Mpower) - motor.powerKw) < 0.01
  ) || null;
};

// Onga's fixed-motor domestic pumps (JS/OME etc) don't have a mounting type
// or swappable motor, so their dimension table (pump-selector-onga-dimensions.json)
// is keyed by model directly rather than pump family + motor poles/kW.
PS.getOngaDimensionRow = function (match) {
  const rows = PS.ongaDimensionData?.dimensions;

  if (!Array.isArray(rows)) return null;

  const model = match.curve?.model || match.model;

  return rows.find(row => row.model === model) || null;
};

// Jung's fixed-motor submergible pumps (MultiFree etc) are the same shape of
// problem as Onga's above - own dimension table (pump-selector-jung-dimensions.json),
// keyed by model directly, since Jung's lettered GA fields (A-Q) don't match
// Onga's (A-F/G1/G2/DNA/DNM) closely enough to share one file/schema.
PS.getJungDimensionRow = function (match) {
  const rows = PS.jungDimensionData?.dimensions;

  if (!Array.isArray(rows)) return null;

  const model = match.curve?.model || match.model;

  return rows.find(row => row.model === model) || null;
};

// Every fixedPump curve (Onga, Jung, and any future domestic-format brand)
// needs a dimension row lookup for the printable sheet's "Unit weight" row
// and the GA drawing modal - dispatches to whichever manufacturer's own
// dimension file actually applies, so callers don't need to know which one.
PS.getFixedPumpDimensionRow = function (match) {
  const manufacturer = match.curve?.manufacturer || match.manufacturer;

  if (manufacturer === 'Jung Pumpen') return PS.getJungDimensionRow(match);

  return PS.getOngaDimensionRow(match);
};

// Best-effort build code combining pump family, motor PU code and the
// mounting/base code from the dimension tables. This has not been validated
// against TAPS's exact official part-number format - treat as a working
// reference code until confirmed.
PS.buildPumpUnitCode = function (match, brand, mountingType) {
  const unitEff = PS.calculateUnitEfficiency(match, brand);

  if (!unitEff || !unitEff.motor) {
    return { code: null, unitEff, dimensionRow: null };
  }

  const puCode = PS.getPuCode(unitEff.motor.powerKw, unitEff.poles);
  const dimensionRow = PS.findDimensionRow(match, mountingType, unitEff.motor);
  const mountingCode = dimensionRow?.Unitcode || dimensionRow?.Basecode || '';
  const pumpCode = PS.getDimensionPumpCode(match.family);

  const parts = [pumpCode, puCode, mountingCode].filter(Boolean);

  return {
    code: parts.length ? parts.join('-') : null,
    unitEff,
    dimensionRow
  };
};
